import { AssetApi } from '../../../api/models/apis';
import { TpTemplateInfo } from '../../../api/models/assets';
import { isModifiableTemplateScope } from '../../../components/miscUtils';
import { TpConfirmData } from '../../../components/message-boxes/ConfirmBox';
import { resolveTemplateForAsset } from '../../../data/templateSlice.helpers';

export const SYSTEM_TEMPLATE_ADMIN_REQUIRED_MESSAGE = 'Only system administrators can delete system templates.';
export const SYSTEM_TEMPLATE_DELETE_WARNING = 'This is a system template.';
export const CURRENT_TEMPLATE_DELETE_WARNING =
  'This template is currently selected for this asset type and may be in use.';
export const ASSETS_EXIST_DELETE_MESSAGE = 'Asset count for this type: {count}.';
export const LAST_TEMPLATE_DELETE_WARNING = 'This is the last remaining template for this asset type.';

interface TemplateDeleteConfirmationDependencies {
  scopeValue: string;
  isSystemAdmin: boolean;
  templatesForType: TpTemplateInfo[];
  allTemplatesForType: TpTemplateInfo[];
  listAssets: AssetApi['listAssets'];
  promptConfirmationBox: (confirmData: TpConfirmData) => Promise<unknown>;
  showErrorMessage: (message: string) => void;
}

export const isSystemTemplateDeletionAllowed = (template: TpTemplateInfo, isSystemAdmin: boolean): boolean =>
  !template.isSystem || isSystemAdmin;

export const isCurrentlyResolvedTemplate = (template: TpTemplateInfo, templatesForType: TpTemplateInfo[]): boolean => {
  const scopeSuffix = template.scope === 'entity' ? 'entity' : 'dashboard';
  return resolveTemplateForAsset(templatesForType, undefined, scopeSuffix)?.id === template.id;
};

export const isLastTemplateOfType = (template: TpTemplateInfo, allTemplatesForType: TpTemplateInfo[]): boolean =>
  allTemplatesForType.length === 1 && allTemplatesForType[0].id === template.id;

/**
 * Provide a confirmation dialog for deleting a template.
 * Specific warnings can be generated and some modify the level of caution required.
 * These are:
 * - SYSTEM_TEMPLATE_DELETE_WARNING: Indicates that the template is a system template.
 * System templates require administrator privileges to delete.
 * - CURRENT_TEMPLATE_DELETE_WARNING: Indicates that the template is currently selected
 * (default when the asset is opened) for this asset type.
 * Deleting it will impact the user experience for this asset type.
 * - ASSETS_EXIST_DELETE_MESSAGE: Indicates that there are existing assets of this type.
 * This is just as reference, it can have an impact on which templates a user might be able to select.
 * - LAST_TEMPLATE_DELETE_WARNING: Indicates that this is the last remaining template for this asset type.
 * (deleting it will leave the asset type without a template)
 */
export async function confirmTemplateDeletion(
  template: TpTemplateInfo,
  dependencies: TemplateDeleteConfirmationDependencies,
): Promise<boolean> {
  const {
    scopeValue,
    isSystemAdmin,
    templatesForType,
    allTemplatesForType,
    listAssets,
    promptConfirmationBox,
    showErrorMessage,
  } = dependencies;

  if (!isSystemTemplateDeletionAllowed(template, isSystemAdmin)) {
    showErrorMessage(SYSTEM_TEMPLATE_ADMIN_REQUIRED_MESSAGE);
    return false;
  }

  const confirmData: TpConfirmData = {
    title: `Delete ${template.name}`,
    description: `Do you really want to delete ${template.name}? \n\n`,
    warningAlert: false,
  };

  if (template.isSystem) {
    confirmData.description += `${SYSTEM_TEMPLATE_DELETE_WARNING}\n\n`;
    confirmData.warningAlert = true;
  }

  if (isModifiableTemplateScope(scopeValue)) {
    const { queryStats } = await listAssets({ limit: 1, offset: 0, typeFilter: template.templateType });
    const assetCount = queryStats[template.templateType] ?? 0;

    if (isCurrentlyResolvedTemplate(template, templatesForType)) {
      confirmData.description += `${CURRENT_TEMPLATE_DELETE_WARNING}\n\n`;
      confirmData.warningAlert = true;
    }

    if (assetCount > 0) {
      confirmData.description += `${ASSETS_EXIST_DELETE_MESSAGE.replace('{count}', String(assetCount))}\n\n`;
      if (isLastTemplateOfType(template, allTemplatesForType)) {
        confirmData.description += `${LAST_TEMPLATE_DELETE_WARNING}\n\n`;
        confirmData.warningAlert = true;
      }
    }
  }

  return Boolean(await promptConfirmationBox(confirmData));
}
